import { useEffect, useState, useRef, CSSProperties, FC } from "react";
import { motion, useAnimation, useInView } from "framer-motion";

type DrawnSvgProps = {
  svgPath: string; // Path to SVG file (public or relative)
  className?: string;
  duration?: number;
  delay?: number;
  width?: number | string;
  height?: number | string;
  ease?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number | string;
  style?: CSSProperties;
  // animation overrides
  animateTransform?: string;
  animateTranslate?: string;
  animateSkew?: string;
  animateOpacity?: number;
  animateFill?: string;
  animateStroke?: string;
  animateStrokeDasharray?: number | string;
  animateStrokeDashoffset?: number | string;
};

type SvgPathData = {
  d: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: string;
  [key: string]: any;
};

function getPathLength(d: string): number {
  // create a temporary SVG path to measure length
  if (typeof document === "undefined") return 1000;
  const svgNS = "http://www.w3.org/2000/svg";
  const path = document.createElementNS(svgNS, "path");
  path.setAttribute("d", d);
  return path.getTotalLength();
}

export const DrawnSvg: FC<DrawnSvgProps> = ({
  svgPath,
  className,
  duration = 1.2,
  delay = 0,
  width,
  height,
  ease = "easeInOut",
  fill,
  stroke = "#000",
  strokeWidth = 2,
  style,
  animateTransform,
  animateTranslate,
  animateSkew,
  animateOpacity,
  animateFill,
  animateStroke,
  animateStrokeDasharray,
  animateStrokeDashoffset,
}) => {
  const [paths, setPaths] = useState<SvgPathData[]>([]);
  const [borderPaths, setBorderPaths] = useState<SvgPathData[]>([]);
  const [viewBox, setViewBox] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<SVGSVGElement | null>(null);
  // changed margin to "0px" to trigger as soon as the element is fully in the viewport
  const inView = useInView(ref, { once: false, margin: "0px" });
  const controls = useAnimation();

  // load and parse SVG
  useEffect(() => {
    let isMounted = true;
    setLoaded(false);
    fetch(svgPath)
      .then((res) => res.text())
      .then((svgText) => {
        if (!isMounted) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, "image/svg+xml");
        const svgEl = doc.querySelector("svg");
        if (svgEl?.getAttribute("viewBox")) {
          setViewBox(svgEl.getAttribute("viewBox")!);
        }
        // collect all <path> elements
        // Border Elements
        const borderPathEls = Array.from(doc.querySelectorAll("g#border path"));
        const borderPathDatas: SvgPathData[] = borderPathEls.map((el) => {
          const d = el.getAttribute("d") || "";
          const fill = el.getAttribute("fill") || undefined;
          const stroke = el.getAttribute("stroke") || undefined;
          const strokeWidth = el.getAttribute("stroke-width") || undefined;
          return { d, fill, stroke, strokeWidth };
        });
        setBorderPaths(borderPathDatas);
        // Inner Elements
        const pathEls = Array.from(doc.querySelectorAll("g#inner path"));
        const pathDatas: SvgPathData[] = pathEls.map((el) => {
          const d = el.getAttribute("d") || "";
          const fill = el.getAttribute("fill") || undefined;
          const stroke = el.getAttribute("stroke") || undefined;
          const strokeWidth = el.getAttribute("stroke-width") || undefined;
          return { d, fill, stroke, strokeWidth };
        });
        setPaths(pathDatas);

        setLoaded(true);
      })
      .catch(() => {
        setPaths([]);
        setLoaded(true);
      });
    return () => {
      isMounted = false;
    };
  }, [svgPath]);

  // animate on inView
  useEffect(() => {
    if (!loaded) return;
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [inView, loaded, controls]);

  // default viewBox fallback
  const defaultViewBox = "0 0 100 100";

  return (
    <motion.svg
      ref={ref}
      className={className}
      width={width}
      height={height}
      viewBox={viewBox || defaultViewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      // draw border paths
      {borderPaths.map((p, i) => {
        // compute path length for dasharray
        const pathLength = getPathLength(p.d);
        // allow per-path fill/stroke override, but prefer props
        const pathFill = animateFill ?? fill ?? p.fill ?? "none";
        const pathStroke = animateStroke ?? stroke ?? p.stroke ?? "#000";
        const pathStrokeWidth = strokeWidth ?? p.strokeWidth ?? 2;
        const dashArray = animateStrokeDasharray ?? pathLength;
        const dashOffset = animateStrokeDashoffset ?? pathLength;

        // compose transform string
        let transform = "";
        if (animateTransform) transform += animateTransform + " ";
        if (animateTranslate) transform += `translate(${animateTranslate}) `;
        if (animateSkew) transform += `skew(${animateSkew}) `;

        return (
          <motion.path
            key={i}
            d={p.d}
            fill={pathFill}
            stroke={pathStroke}
            strokeWidth={pathStrokeWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            initial="hidden"
            animate={controls}
            variants={{
              hidden: {
                strokeDashoffset: dashArray,
                opacity: animateOpacity ?? 0,
                fill: pathFill,
                stroke: pathStroke,
                transform,
              },
              visible: {
                strokeDashoffset: 0,
                opacity: animateOpacity ?? 1,
                fill: pathFill,
                stroke: pathStroke,
                transform,
                transition: {
                  strokeDashoffset: {
                    delay,
                    duration,
                    ease,
                  },
                  opacity: {
                    delay,
                    duration: duration * 0.7,
                    ease,
                  },
                  fill: {
                    delay: delay + duration * 0.7,
                    duration: duration * 0.3,
                    ease,
                  },
                  stroke: {
                    delay,
                    duration,
                    ease,
                  },
                  transform: {
                    delay,
                    duration,
                    ease,
                  },
                },
              },
            }}
          />
        );
      })}
      {paths.map((p, i) => {
        // compute path length for dasharray
        const pathLength = getPathLength(p.d);
        // allow per-path fill/stroke override, but prefer props
        const pathFill = animateFill ?? fill ?? p.fill ?? "none";
        const pathStroke = animateStroke ?? stroke ?? p.stroke ?? "#000";
        const pathStrokeWidth = p.strokeWidth ?? 0;
        const dashArray = animateStrokeDasharray ?? pathLength;
        const dashOffset = animateStrokeDashoffset ?? pathLength;

        // compose transform string
        let transform = "";
        if (animateTransform) transform += animateTransform + " ";
        if (animateTranslate) transform += `translate(${animateTranslate}) `;
        if (animateSkew) transform += `skew(${animateSkew}) `;

        return (
          <motion.path
            key={i}
            d={p.d}
            fill={pathFill}
            stroke={pathStroke}
            strokeWidth={pathStrokeWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            initial="hidden"
            animate={controls}
            variants={{
              hidden: {
                strokeDashoffset: dashArray,
                opacity: animateOpacity ?? 0,
                fill: pathFill,
                stroke: pathStroke,
                transform,
              },
              visible: {
                strokeDashoffset: 0,
                opacity: animateOpacity ?? 1,
                fill: pathFill,
                stroke: pathStroke,
                transform,
                transition: {
                  opacity: {
                    delay: delay * 2,
                    duration: duration * 0.7,
                    ease,
                  },
                  transform: {
                    delay,
                    duration,
                    ease,
                  },
                },
              },
            }}
          />
        );
      })}
    </motion.svg>
  );
};
